import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@/components/ui/dialog';
import { DialogTitleWithBug } from '@/components/ui/dialog-title-with-bug';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useReferralProgramEdit } from '@/contexts/ReferralProgramEditContext';
import { useReferralPrograms } from '@/hooks/useReferralPrograms';
import { useBrandSelection } from '@/hooks/useBrandSelection';
import { useToast } from '@/hooks/use-toast';
import { useTypedTranslation } from '@/lib/i18n/hooks';
import type { ReferralProgram, ReferralMilestone } from '@/types/referral';
import {
  MilestoneTiersEditor,
  type MilestoneNudgeConfig,
  type MilestoneCelebrationConfig,
} from '@/components/referral/MilestoneTiersEditor';
import { cn } from '@/lib/utils';

const defaultNudge: MilestoneNudgeConfig = { enabled: false, within: 2, subject: '', content: '' };
const defaultCelebration: MilestoneCelebrationConfig = { enabled: false, subject: '', content: '' };

interface FormState {
  name: string;
  type: ReferralProgram['type'];
  description: string;
  milestones: ReferralMilestone[];
  nudgeConfig: MilestoneNudgeConfig;
  celebrationConfig: MilestoneCelebrationConfig;
}

const emptyForm: FormState = {
  name: '',
  type: 'generic',
  description: '',
  milestones: [],
  nudgeConfig: { ...defaultNudge },
  celebrationConfig: { ...defaultCelebration },
};

export function GlobalReferralProgramEditDialog() {
  const { isOpen, mode, editingProgram, closeEditProgram } = useReferralProgramEdit();
  const { currentBrandId } = useBrandSelection();
  const { updateProgram, createProgram } = useReferralPrograms(currentBrandId);
  const { toast } = useToast();
  const { t } = useTypedTranslation();

  const [form, setForm] = useState<FormState>({ ...emptyForm });

  const isCreate = mode === 'create';

  useEffect(() => {
    if (!isOpen) return;
    if (isCreate) {
      setForm({ ...emptyForm });
    } else if (editingProgram) {
      const s = (editingProgram.settings as Record<string, unknown>) ?? {};
      setForm({
        name: editingProgram.name,
        type: editingProgram.type,
        description: editingProgram.description ?? '',
        milestones: ((s.milestones as ReferralMilestone[]) ?? []).slice(),
        nudgeConfig: {
          enabled: !!(s.milestone_nudge_enabled),
          within: (s.milestone_nudge_within as number) ?? 2,
          subject: (s.milestone_nudge_subject as string) ?? '',
          content: (s.milestone_nudge_content as string) ?? '',
        },
        celebrationConfig: {
          enabled: !!(s.milestone_celebration_enabled),
          subject: (s.milestone_celebration_subject as string) ?? "Congratulations! You've unlocked {reward}!",
          content: (s.milestone_celebration_content as string) ?? '',
        },
      });
    }
  }, [editingProgram, isOpen, isCreate]);

  const buildSettings = () => {
    if (form.type !== 'milestone') return undefined;
    const baseSettings = isCreate ? {} : ((editingProgram?.settings as Record<string, unknown>) ?? {});
    return {
      ...baseSettings,
      milestones: form.milestones,
      milestone_nudge_enabled: form.nudgeConfig.enabled,
      milestone_nudge_within: form.nudgeConfig.within,
      milestone_nudge_subject: form.nudgeConfig.subject,
      milestone_nudge_content: form.nudgeConfig.content,
      milestone_celebration_enabled: form.celebrationConfig.enabled,
      milestone_celebration_subject: form.celebrationConfig.subject,
      milestone_celebration_content: form.celebrationConfig.content,
    };
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: t('referral.common.error'), description: t('referral.programs.nameRequired'), variant: 'destructive' });
      return;
    }

    const settings = buildSettings();

    if (isCreate) {
      if (!currentBrandId) {
        toast({ title: t('referral.common.error'), description: t('referral.programs.noBrandSelected'), variant: 'destructive' });
        return;
      }
      const result = await createProgram({
        name: form.name,
        type: form.type,
        description: form.description || undefined,
        settings,
      });
      if (result && !('error' in result)) {
        toast({ title: t('referral.common.success'), description: t('referral.programs.programCreated') });
        closeEditProgram();
      } else {
        const message = result && 'error' in result ? result.error : t('referral.programs.failedToCreateProgram');
        toast({ title: t('referral.common.error'), description: message, variant: 'destructive' });
      }
    } else {
      if (!editingProgram) return;
      const ok = await updateProgram(editingProgram.id, {
        name: form.name,
        type: form.type,
        description: form.description || undefined,
        settings: settings ?? { milestones: undefined, milestone_nudge_enabled: false },
      });
      if (ok) {
        toast({ title: t('referral.common.success'), description: t('referral.programs.programUpdated') });
        closeEditProgram();
      }
    }
  };

  const showMilestone = form.type === 'milestone';

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeEditProgram()}>
      <DialogContent
        className={cn(
          'max-h-[90vh] overflow-hidden transition-all duration-300 ease-in-out flex flex-col',
          showMilestone ? 'max-w-4xl' : 'max-w-lg'
        )}
      >
        <DialogHeader>
          <DialogTitleWithBug
            popupName={isCreate ? 'Create Referral Program' : 'Edit Referral Program'}
            popupContext="/referral/programs"
            isOpen={isOpen}
          >
            {isCreate ? t('referral.programs.newProgramTitle') : t('referral.programs.editProgramTitle')}
          </DialogTitleWithBug>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="flex gap-6">
            {/* Left: Main form */}
            <div className={cn('space-y-4 py-2 transition-all duration-300', showMilestone ? 'w-1/2 min-w-0' : 'w-full')}>
              {isCreate || !showMilestone ? (
                <>
                  <div>
                    <Label>{t('referral.programs.name')}</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder={t('referral.programs.programNamePlaceholder')}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>{t('referral.programs.type')}</Label>
                    <Select
                      value={form.type}
                      onValueChange={(v: ReferralProgram['type']) =>
                        setForm((f) => ({ ...f, type: v, milestones: v === 'milestone' ? f.milestones : [] }))
                      }
                    >
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="generic">{t('referral.programs.generic')}</SelectItem>
                        <SelectItem value="milestone">{t('referral.programs.milestone')}</SelectItem>
                        <SelectItem value="waiting_list">{t('referral.programs.waitingList')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('referral.programs.descriptionOptional')}</Label>
                    <Textarea
                      value={form.description}
                      onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                      placeholder={t('referral.programs.descriptionPlaceholder')}
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                </>
              ) : (
                /* Edit mode with tabs when milestone */
                <Tabs defaultValue="general" className="w-full">
                  <TabsList className="w-full">
                    <TabsTrigger value="general">{t('referral.programs.tabGeneral')}</TabsTrigger>
                  </TabsList>
                  <TabsContent value="general" className="space-y-4 pt-4">
                    <div>
                      <Label>{t('referral.programs.name')}</Label>
                      <Input
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>{t('referral.programs.type')}</Label>
                      <Select
                        value={form.type}
                        onValueChange={(v: ReferralProgram['type']) =>
                          setForm((f) => ({ ...f, type: v, milestones: v === 'milestone' ? f.milestones : [] }))
                        }
                      >
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="generic">{t('referral.programs.generic')}</SelectItem>
                          <SelectItem value="milestone">{t('referral.programs.milestone')}</SelectItem>
                          <SelectItem value="waiting_list">{t('referral.programs.waitingList')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t('referral.programs.description')}</Label>
                      <Textarea
                        value={form.description}
                        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                  </TabsContent>
                </Tabs>
              )}
            </div>

            {/* Right: Milestone tiers panel - slides in */}
            <div
              className={cn(
                'transition-all duration-300 ease-in-out overflow-hidden border-l border-border',
                showMilestone ? 'w-1/2 opacity-100 pl-6' : 'w-0 opacity-0 pl-0'
              )}
            >
              {showMilestone && (
                <div className="py-2 overflow-y-auto max-h-[60vh]">
                  <MilestoneTiersEditor
                    milestones={form.milestones}
                    onChange={(m) => setForm((f) => ({ ...f, milestones: m }))}
                    nudgeConfig={form.nudgeConfig}
                    onNudgeConfigChange={(c) => setForm((f) => ({ ...f, nudgeConfig: c }))}
                    celebrationConfig={form.celebrationConfig}
                    onCelebrationConfigChange={(c) => setForm((f) => ({ ...f, celebrationConfig: c }))}
                    brandId={currentBrandId}
                    programId={editingProgram?.id ?? null}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={closeEditProgram}>
            {t('referral.campaigns.cancel')}
          </Button>
          <Button onClick={handleSave}>
            {isCreate ? t('referral.programs.create') : t('referral.lists.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
